import React, { useState, useContext, useMemo, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityOperationBar from "../../components/EntityOperationBar";
import "../../starek.css";
import DataTable from "react-data-table-component";
import moment from "moment";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getAvailableIntegrations } from "../../services/RepositoryService";
import IntegrationSidebar, { DELETE_ONE_INTEGRATION } from "./IntegrationSidebar";
import IntegrationsCardView, { IntegrationsEmptyState } from "./IntegrationsCardView";
import IntegrationsViewToggle from "./IntegrationsViewToggle";

const GET_INTEGRATIONS_LIST = gql`
    query GetIntegrations($where: IntegrationWhereInput!) {
        integrations(where: $where) {
            id
            name
            description
            integration_key
            is_active
            created_at
        }
    }
`;

const UPDATE_MANY_INTEGRATION = gql`
    mutation UpdateManyIntegration($data: IntegrationUpdateManyMutationInput!, $where: IntegrationWhereInput) {
        updateManyIntegration(data: $data, where: $where) {
            count
        }
    }
`;

// Integration has no deleted_at field (unlike Zone/Part/DeviceProfile) —
// is_active here is a real pause/resume dispatch switch, not soft-delete
// (see IntegrationMiddleware.ts, which only fires for is_active: true rows),
// so the toggle only ever sets is_active, not utils/softDelete.js's
// deleted_at-setting helpers.
const deactivateData = () => ({ is_active: { set: false } });
const activateData = () => ({ is_active: { set: true } });

const Integrations = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: urlId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState("card");
    const [showInactive, setShowInactive] = useState(false);
    const [templates, setTemplates] = useState([]);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam === "card" || viewParam === "list") setViewMode(viewParam);
    }, [searchParams]);

    useEffect(() => {
        getAvailableIntegrations().then((data) => setTemplates(data || []));
    }, []);

    const templatesByKey = useMemo(
        () => Object.fromEntries(templates.map((t) => [t.key, t])),
        [templates],
    );

    const integrationsWhere = useMemo(
        () => ({
            plant_id: { equals: starekContext.lastUsedPlant.id },
            ...(showInactive ? {} : { is_active: { equals: true } }),
        }),
        [starekContext.lastUsedPlant.id, showInactive],
    );

    const { data, loading, error, refetch } = useQuery(GET_INTEGRATIONS_LIST, {
        client: starekContext.engineClient,
        variables: { where: integrationsWhere },
        fetchPolicy: "network-only",
    });

    const [deleteOneIntegration] = useMutation(DELETE_ONE_INTEGRATION, {
        client: starekContext.engineClient,
    });
    const [updateManyIntegration] = useMutation(UPDATE_MANY_INTEGRATION, {
        client: starekContext.engineClient,
    });

    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [togglingActiveId, setTogglingActiveId] = useState(null);

    const [showSidebar, setShowSidebar] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const integrations = data?.integrations ?? [];

    // Resolve the template for the sidebar: from the row being edited, or
    // from ?template=<key> when creating a new integration from the catalog.
    const templateParam = searchParams.get("template");
    const sidebarTemplate = editingItem?.integration_key
        ? templatesByKey[editingItem.integration_key]
        : templateParam
        ? templatesByKey[templateParam]
        : undefined;

    // Open sidebar when navigating to /integrations/:id
    useEffect(() => {
        if (urlId != null) {
            const id = Number(urlId);
            if (id === 0) {
                setEditingItem(null);
                setShowSidebar(true);
            } else if (!loading && integrations.length > 0) {
                const found = integrations.find((w) => w.id === id);
                setEditingItem(found ?? { id });
                setShowSidebar(true);
            }
        }
    }, [urlId, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return integrations;
        return integrations.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [integrations, filterText]);

    const openEdit = () => {
        if (selectedRows.length !== 1) return;
        setEditingItem(selectedRows[0]);
        setShowSidebar(true);
        navigate(`/integrations/${selectedRows[0].id}`);
    };

    const closeSidebar = () => {
        setShowSidebar(false);
        setEditingItem(null);
        navigate("/integrations");
    };

    const handleSave = async () => {
        await refetch();
        closeSidebar();
    };

    const handleDeleteFromSidebar = async (item) => {
        await deleteOneIntegration({ variables: { where: { id: item.id } } });
        await refetch();
        closeSidebar();
    };

    const handleBulkDelete = async () => {
        setIsDeleting(true);
        setDeleteDone(0);
        try {
            for (const integration of selectedRows) {
                await deleteOneIntegration({ variables: { where: { id: integration.id } } });
                setDeleteDone((n) => n + 1);
            }
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
        } catch (err) {
            starekContext.showError(err?.message || err);
        } finally {
            setIsDeleting(false);
            setDeleteDone(0);
        }
    };

    const handleToggleActive = async (rows, makeActive) => {
        const ids = rows.map((r) => r.id);
        try {
            await updateManyIntegration({
                variables: {
                    data: makeActive ? activateData() : deactivateData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(
                makeActive ? `Restored ${ids.length} integration(s)` : `Deactivated ${ids.length} integration(s)`,
            );
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
        } catch (err) {
            starekContext.showError(err?.message || err);
        }
    };

    const handleToggleActiveSingle = async (integration) => {
        setTogglingActiveId(integration.id);
        try {
            await updateManyIntegration({
                variables: {
                    data: integration.is_active ? deactivateData() : activateData(),
                    where: { id: { in: [integration.id] } },
                },
            });
            await refetch();
        } catch (err) {
            starekContext.showError(err?.message || err);
        } finally {
            setTogglingActiveId(null);
        }
    };

    const columns = [
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => {
                const template = templatesByKey[row.integration_key];
                return (
                    <div
                        className="d-flex align-items-center cursor-pointer"
                        onClick={() => {
                            setEditingItem(row);
                            setShowSidebar(true);
                            navigate(`/integrations/${row.id}`);
                        }}
                    >
                        <span className="w-30px h-30px me-2 d-flex align-items-center justify-content-center rounded-circle border bg-color-f2f2f2">
                            {template?.icon ? (
                                <img src={template.icon} alt="" className="w-18px h-18px object-fit-contain" />
                            ) : (
                                <i className="fa fa-plug" />
                            )}
                        </span>
                        <span>{row.name}</span>
                    </div>
                );
            },
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
        },
        {
            name: "Type",
            selector: (row) => templatesByKey[row.integration_key]?.name ?? row.integration_key,
            sortable: true,
            cell: (row) => {
                const template = templatesByKey[row.integration_key];
                return template ? (
                    <span className="badge bg-info text-white">{template.name}</span>
                ) : (
                    <span className="badge bg-secondary">{row.integration_key}</span>
                );
            },
        },
        {
            name: "Status",
            selector: (row) => row.is_active,
            sortable: true,
            width: "110px",
            cell: (row) => (
                <span className={`badge bg-${row.is_active ? "success" : "secondary"}`}>
                    {row.is_active ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            name: "Created",
            selector: (row) => moment.utc(row.created_at).format("YYYY-MM-DD HH:mm"),
            sortable: true,
            width: "160px",
        },
    ];

    const customStyles = { headCells: { style: { fontWeight: "bold" } } };
    const conditionalRowStyles = [
        { when: (r) => !r.is_active, style: { opacity: 0.5, backgroundColor: "#f8f9fa" } },
    ];

    if (loading) return <EntityLoadingState icon="fa-plug" />;

    if (error) {
        return (
            <div className="tw-text-center tw-mt-10 tw-text-red-600">
                Failed to load integrations: {error.message}
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">My Integrations</div>

            <EntityOperationBar
                entity={{ singular: "Integration", plural: "Integrations" }}
                allowSearch
                allowAdd={false}
                allowDelete
                allowEdit
                allowShowInactive
                showInactive={showInactive}
                onShowInactiveChange={(v) => {
                    setShowInactive(v);
                    setSelectedRows([]);
                }}
                allowExtendedActions
                extendedActions={[
                    {
                        label: "Deactivate",
                        icon: "fa-ban",
                        danger: true,
                        disabled: selectedRows.every((r) => !r.is_active),
                        onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
                    },
                    {
                        label: "Restore",
                        icon: "fa-undo",
                        disabled: selectedRows.every((r) => r.is_active),
                        onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
                    },
                ]}
                extraContent={<IntegrationsViewToggle current="my" />}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onEdit={openEdit}
                onDelete={handleBulkDelete}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    setSearchParams({ view: mode });
                }}
            >
                <EntityOperationBar.View mode="card" icon="fa-th" tooltip="Card View">
                    <IntegrationsCardView
                        items={integrations}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                        filterText={filterText}
                        templatesByKey={templatesByKey}
                        togglingActiveId={togglingActiveId}
                        onToggleActive={handleToggleActiveSingle}
                    />
                </EntityOperationBar.View>
                <EntityOperationBar.View mode="list" icon="fa-list-ul" tooltip="List View">
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        selectableRows
                        onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                        clearSelectedRows={toggleCleared}
                        pagination
                        highlightOnHover
                        striped
                        responsive
                        customStyles={customStyles}
                        conditionalRowStyles={conditionalRowStyles}
                        noDataComponent={<IntegrationsEmptyState isFiltered={!!filterText.trim()} />}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <IntegrationSidebar
                show={showSidebar}
                item={editingItem}
                template={sidebarTemplate}
                onSave={handleSave}
                onDelete={handleDeleteFromSidebar}
                onDiscard={closeSidebar}
            />
        </div>
    );
};

export { Integrations };
